import { useEffect, useCallback } from 'react';
import * as yup from 'yup';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Grid, IconButton, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { CustomModal } from '../../../../../../../components';
import { getEventById, updateEvent } from '../../../../../../../core/events/action';

const initialFormValues = {
  booking_fee_percentage: '',
  cancellation_fee_percentage: '',
};

const eventFeesValidationSchema = yup.object({
  booking_fee_percentage: yup
    .number('Enter the booking fee percentage')
    .required('Booking fee percentage is Required')
    .min(0, 'Booking fee must be greater than or equal to 0')
    .max(100, 'Booking fee must be less than or equal to 100'),
  cancellation_fee_percentage: yup
    .number('Enter the cancellation fee percentage')
    .required('Cancellation fee percentage is Required')
    .min(0, 'Cancellation fee must be greater than or equal to 0')
    .max(100, 'Cancellation fee must be less than or equal to 100'),
});

export default function FeesFormModal({ showFeesFormModal, closeFeesFormModal, eventId }) {
  const dispatch = useDispatch();
  const {
    events: { event, loading, successMessage, failedMessage },
  } = useSelector((store) => store);
  const CustomizedSwal = withReactContent(Swal);
  const successNotification = useCallback(
    () => {
      if (!event?.id) return;
      CustomizedSwal.fire({
        title: <strong>{event.name}</strong>,
        text: 'Event saved successful!',
        icon: 'success',
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps, prettier/prettier
    [CustomizedSwal]
  );

  const failedNotification = useCallback(() => {
    if (!event?.id) return;
    CustomizedSwal.fire({
      title: <strong>{event.name}</strong>,
      text: 'Event not saved, please verify the info',
      icon: 'error',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CustomizedSwal]);

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: eventFeesValidationSchema,
    onSubmit: (values) => {
      const payload = {
        id: event.id,
        booking_fee_percentage: Number(values.booking_fee_percentage),
        cancellation_fee_percentage: Number(values.cancellation_fee_percentage),
        // eslint-disable-next-line prettier/prettier
      };
      dispatch(updateEvent(payload));
    },
  });

  const onCloseModal = () => {
    closeFeesFormModal();
    formik.resetForm();
  };

  useEffect(() => {
    if (eventId) {
      dispatch(getEventById(eventId));
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    if (event?.id) {
      formik.setValues({
        booking_fee_percentage: event.booking_fee_percentage,
        cancellation_fee_percentage: event.cancellation_fee_percentage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  useEffect(() => {
    if (successMessage) {
      successNotification();
      onCloseModal();
    } else if (failedMessage) {
      failedNotification();
      onCloseModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMessage, failedMessage]);

  return (
    <CustomModal isOpen={showFeesFormModal} handleClose={onCloseModal}>
      <Grid container justifyContent="center" rowGap={1}>
        <Grid item xs={12}>
          <IconButton onClick={onCloseModal}>
            <CancelOutlinedIcon sx={{ fill: '#0997ba' }} />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item>
          <div style={{ marginBottom: '1rem' }}>
            <Typography fontWeight="bold">Update Fees</Typography>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              type="number"
              style={{ marginBottom: '1rem' }}
              fullWidth
              label="Booking fee percentage"
              name="booking_fee_percentage"
              value={formik.values.booking_fee_percentage || ''}
              onChange={formik.handleChange}
              variant="standard"
              size="small"
              error={
                formik.touched.booking_fee_percentage &&
                Boolean(formik.errors.booking_fee_percentage)
              }
              helperText={
                formik.touched.booking_fee_percentage && formik.errors.booking_fee_percentage
              }
            />
            <TextField
              type="number"
              style={{ marginBottom: '1rem' }}
              fullWidth
              label="Cancellation fee percentage"
              name="cancellation_fee_percentage"
              value={formik.values.cancellation_fee_percentage || ''}
              onChange={formik.handleChange}
              variant="standard"
              size="small"
              error={
                formik.touched.cancellation_fee_percentage &&
                Boolean(formik.errors.cancellation_fee_percentage)
              }
              helperText={
                formik.touched.cancellation_fee_percentage &&
                formik.errors.cancellation_fee_percentage
              }
            />
            <Button type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </form>
        </Grid>
      </Grid>
    </CustomModal>
  );
}

FeesFormModal.propTypes = {
  showFeesFormModal: PropTypes.bool,
  closeFeesFormModal: PropTypes.func,
  eventId: PropTypes.number,
};

FeesFormModal.defaultProps = {
  eventId: null,
  closeFeesFormModal: () => {},
  showFeesFormModal: false,
};
